import {
  ExerciseAssetType,
  PaginatedListOfExerciseAssetDto,
} from "@growth-machine-llc/stridist-api-client";
import { useOptimisticUpdateMutation } from "../../../hooks/useOptimisticUpdateMutation";
import { useQueryClient } from "@tanstack/react-query";
import { COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY } from "../ExercisesTableScreen";
import ExerciseAssetsService from "../../../services/ExerciseAssetsService";
import { INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE } from "./useArchiveRestoreLibraryExercise";

const getTypesToInvalidate = (type: ExerciseAssetType) => {
  return [ExerciseAssetType.ALL, ExerciseAssetType.TEAM, ExerciseAssetType.PRIVATE].filter((t) => t !== type)
};

export const useToggleTeamExerciseVisibilityMutation = (
  exerciseType: ExerciseAssetType,
  disableToastAlerts: boolean = false,
) => {
  const queryClient = useQueryClient();

  const exerciseTypesToInvalidate = getTypesToInvalidate(exerciseType);

  return useOptimisticUpdateMutation({
    queryKey: [COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY, { exerciseType }],
    invalidateQueryOptions: {
      enabled: true,
      refetchType: "none",
    },
    disableToastAlerts,
    mutationFn: ExerciseAssetsService.toggleTeamExerciseVisibility,
    optimisticUpdater: {
      updateFn: (oldData: PaginatedListOfExerciseAssetDto, variables) => {
        const shouldRemoveExercise =
          exerciseType === ExerciseAssetType.TEAM && !variables.isTeamShared;

        const items = shouldRemoveExercise
          ? oldData.items.filter((ex) => ex.id !== variables.id)
          : oldData.items.map((ex) =>
              ex.id === variables.id
                ? { ...ex, isTeamShared: variables.isTeamShared }
                : ex,
            );

        return { ...oldData, items } as PaginatedListOfExerciseAssetDto;
      },
    },

    options: {
      onSuccess: () => {
        exerciseTypesToInvalidate.forEach((type) => {
          queryClient.invalidateQueries({
            queryKey: [
              COACH_EXERCISE_LIBRARY_ASSETS_QUERY_KEY,
              { exerciseType: type },
            ],
            exact: false,
          });
        });

        INVALIDATE_KEYS_ON_EXERCISE_LIST_UPDATE.forEach((queryKey) => {
          queryClient.invalidateQueries({
            queryKey: [queryKey],
            exact: false,
          });
        });
      },
    },
  });
};
